import { Alert, Box, CircularProgress, Container } from '@mui/material';
import { useEffect, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import TemplateFixtureReplayClient, {
    NORTHSTAR_TEMPLATE_REPLAY_ORGANIZATION,
    NORTHSTAR_TEMPLATE_REPLAY_CRITERIA,
    NORTHSTAR_TEMPLATE_REPLAY_VERSION,
    TemplateFixtureReplay,
} from '../apiClients/TemplateFixtureReplayClient';
import SyntheticTemplateReplayPresentation from '../components/template/SyntheticTemplateReplayPresentation';
import { useAccessInfo } from '../contexts/AccessTokenProvider';
import { Securables } from '../Enums';

const EXPECTED_ABILITY_LEGEND = ['Unskilled', 'Beginner', 'Intermediate', 'Advanced', 'Expert'];
const EXPECTED_BEHAVIOR_LEGEND = ['Never', 'Rarely', 'Sometimes', 'Usually', 'Always'];

const matchesLockedCriteria = (replay: TemplateFixtureReplay) =>
    replay.Criteria.length === NORTHSTAR_TEMPLATE_REPLAY_CRITERIA.length
    && replay.Criteria.every((criterion, index) => {
        const expected = NORTHSTAR_TEMPLATE_REPLAY_CRITERIA[index];
        return criterion.Group === expected.Group
            && criterion.Type === expected.Type
            && criterion.IsCounterproductive === expected.IsCounterproductive
            && criterion.Name === expected.Name
            && criterion.Weight === expected.Weight
            && criterion.SelectedRating === expected.SelectedRating;
    });

export const isCaptureSafeTemplateReplay = (replay: TemplateFixtureReplay, selectedOrganizationId: string) =>
    replay.Version === NORTHSTAR_TEMPLATE_REPLAY_VERSION
    && replay.IsSynthetic === true
    && replay.ReadOnly === true
    && replay.OrganizationId === selectedOrganizationId
    && replay.OrganizationName === NORTHSTAR_TEMPLATE_REPLAY_ORGANIZATION
    && matchesLockedCriteria(replay)
    && JSON.stringify(replay.AbilityLegend) === JSON.stringify(EXPECTED_ABILITY_LEGEND)
    && JSON.stringify(replay.BehaviorLegend) === JSON.stringify(EXPECTED_BEHAVIOR_LEGEND);

export const replayForSelectedOrganization = (
    replay: TemplateFixtureReplay | null,
    loadedOrganizationId: string | null,
    selectedOrganizationId: string,
) => replay !== null
    && loadedOrganizationId === selectedOrganizationId
    && isCaptureSafeTemplateReplay(replay, selectedOrganizationId)
        ? replay
        : null;

export const SyntheticTemplateReplayRoute = () => {
    const { hasOneOfRoles } = useAccessInfo();
    if (!hasOneOfRoles([Securables.organization_administrator, Securables.synergist_admin], true))
        return <Redirect to="/" />;
    return <SyntheticTemplateReplayPage />;
};

const SyntheticTemplateReplayPage = () => {
    const history = useHistory();
    const { accessInfo } = useAccessInfo();
    const [replay, setReplay] = useState<TemplateFixtureReplay | null>(null);
    const [replayOrganizationId, setReplayOrganizationId] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);
    const [failedOrganizationId, setFailedOrganizationId] = useState<string | null>(null);
    const selectedOrganizationId = accessInfo?.selectedOrganizationId ?? '';

    useEffect(() => {
        const controller = new AbortController();
        setReplay(null);
        setReplayOrganizationId(null);
        setFailed(false);
        setFailedOrganizationId(null);
        new TemplateFixtureReplayClient().Get(controller.signal)
            .then(result => {
                if (controller.signal.aborted)
                    return;
                if (!isCaptureSafeTemplateReplay(result, selectedOrganizationId)) {
                    setFailed(true);
                    setFailedOrganizationId(selectedOrganizationId);
                    return;
                }
                setReplay(result);
                setReplayOrganizationId(selectedOrganizationId);
            })
            .catch(() => {
                if (!controller.signal.aborted) {
                    setFailed(true);
                    setFailedOrganizationId(selectedOrganizationId);
                }
            });
        return () => controller.abort();
    }, [selectedOrganizationId]);

    const currentReplay = replayForSelectedOrganization(replay, replayOrganizationId, selectedOrganizationId);
    if (failed && failedOrganizationId === selectedOrganizationId) return <Container maxWidth="md" sx={{ py: 4 }}><Alert severity="warning">Synthetic template replay is unavailable for this organization.</Alert></Container>;
    if (!currentReplay) return <Box role="status" aria-label="Loading synthetic template replay" sx={{ minHeight: 300, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;

    return <SyntheticTemplateReplayPresentation replay={currentReplay} onClose={() => history.push('/TemplateManagement')} />;
};

export default SyntheticTemplateReplayPage;
